import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CrudService from "services/cruds-service";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import {
    Grid,
    Card,
    Box,
    Chip,
    Button,
    TextField,
    InputAdornment,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PublicIcon from "@mui/icons-material/Public";
import PeopleIcon from "@mui/icons-material/People";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { Doughnut, Bar, Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    PointElement,
    LineElement,
    Tooltip as ChartTooltip,
    Legend,
} from "chart.js";
import dayjs from "dayjs";
import "dayjs/locale/tr";
import isoWeek from "dayjs/plugin/isoWeek";
import advancedFormat from "dayjs/plugin/advancedFormat";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { CrmPageHeader, CrmKpiTile } from "../shared/CrmUi";
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    PointElement,
    LineElement,
    ChartTooltip,
    Legend);
dayjs.extend(isoWeek);
dayjs.extend(advancedFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
const cleanStr = (v) => {
    if (v == null) return "";
    const s = String(v).trim();
    if (!s) return "";
    if (s.toLowerCase() === "unknown") return "";
    return s;
};
const firstNonEmpty = (...vals) => {
    for (const v of vals) {
        const s = cleanStr(v);
        if (s) return s;
    }
    return "";
};
const fmt2 = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const SAFE_CHIP_COLORS = new Set(["default", "primary", "secondary", "error", "info", "success", "warning"]);
const safeChipColor = (c) => (SAFE_CHIP_COLORS.has(c) ? c : "default");

const makeColors = (n) => {
    const arr = [];
    for (let i = 0; i < n; i += 1) {
        const hue = Math.round((360 * i) / Math.max(1, n));
        arr.push(`hsl(${hue}, 65%, 55%)`);
    }
    return arr;
};

// Chart options factory
const chartOptions = (type = "doughnut", onClickHandler) => ({
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
        legend: {
            position: "bottom",
            fullSize: false,
            labels: { boxWidth: 14, padding: 12, font: { size: 12 } },
        },
    },
    layout: { padding: { bottom: 20 } },
    onClick: (event, elements, chart) => {
        if (!elements?.length) return;
        const idx = elements[0]?.index;
        const label = chart?.data?.labels?.[idx];
        if (label && onClickHandler) onClickHandler(String(label));
    },
    ...(type === "bar" && {
        scales: { x: { grid: { display: false } }, y: { grid: { color: "#eee" } } },
    }),
});
const LEAD_STATUS_META = {
    New: { label: "New", color: "#1E88E5" },
    Sent: { label: "Sent", color: "#8E24AA" },
    "In progress": { label: "In progress", color: "#FFB300" },
    Converted: { label: "Converted", color: "#43A047" },
    Lost: { label: "Lost", color: "#E53935" },
};

const chipColor = (type) => {
    switch (String(type || "").trim()) {
        case "leadStatus":
            return "secondary";
        case "serviceMode":
            return "warning";
        case "tradeType":
            return "info";
        case "originCountry":
            return "success";
        case "destinationCountry":
            return "warning";
        case "sourceType":
            return "info";
        case "partyType":
            return "error";
        case "requestedMonth":
            return "info";
        case "requestedWeek":
            return "info";
        case "salesRep":
            return "success";
        case "customer":
            return "info";
        default:
            return "default";
    }
};

// KPI Card
// Görünüm CRM / Finance KPI kartıyla aynı; ikonun MUI rengi kartın tonuna çevrilir
const ICON_TONES = { primary: "blue", success: "green", warning: "amber", error: "red", info: "blue" };
const KPICard = ({ title, value, icon, trend }) => (
    <CrmKpiTile
        title={title}
        value={value}
        subtitle={trend || null}
        tone={ICON_TONES[icon?.props?.color] || "navy"}
        icon={icon ? React.cloneElement(icon, { color: "inherit", fontSize: "small" }) : null}
    />
);

// salesRep normalize helper
const normalizeSalesRepNames = (a) => {
    const raw =
        (Array.isArray(a?.salesRep) && a.salesRep) ||
        (Array.isArray(a?.salesReps) && a.salesReps) ||
        (Array.isArray(a?.salesRepresentatives) && a.salesRepresentatives) ||
        (Array.isArray(a?.leadRepresentative) && a.leadRepresentative) ||
        (Array.isArray(a?.leadRepresentatives) && a.leadRepresentatives) ||
        [];

    const names = raw
        .map((x) => {
            if (typeof x === "string") return cleanStr(x);
            if (!x) return "";
            return firstNonEmpty(x.name, x.fullName, x.username, x.email);
        })
        .map((s) => s.trim())
        .filter(Boolean);

    return Array.from(new Set(names));
};

export default function SalesLeadDashboard() {
    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalSearch, setGlobalSearch] = useState("");
    const [chartFilters, setChartFilters] = useState([]);
    const [dateField, setDateField] = useState("createdAt");
    const [dateStart, setDateStart] = useState("");
    const [dateEnd, setDateEnd] = useState("");
    const pickArray = (resp) => {
        const d = resp?.data;
        if (Array.isArray(d)) return d;
        if (Array.isArray(d?.data)) return d.data;
        if (Array.isArray(d?.items)) return d.items;
        return [];
    };

    const fetchLeads = async () => {
        if (typeof CrudService?.getLeads === "function") return CrudService.getLeads();
        if (typeof CrudService?.getSalesLeads === "function") return CrudService.getSalesLeads();
        if (typeof CrudService?.listSalesLeads === "function") return CrudService.listSalesLeads();
        throw new Error("CrudService içinde getLeads/getSalesLeads/listSalesLeads bulunamadı.");
    };

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const res = await fetchLeads();
                const data = pickArray(res);

                const normalized = (Array.isArray(data) ? data : []).map((item) => {
                    const a = item?.attributes || item;
                    const id = item?.id || a?.id || a?._id;
                    const customer = firstNonEmpty(a?.customer, a?.company, a?.selectedCustomer?.name, a?.customerName, "Unknown");
                    const leadStatus = firstNonEmpty(a?.leadStatus, "New");
                    const tradeType = firstNonEmpty(a?.tradeType);
                    const incoterm = firstNonEmpty(a?.incoterm);
                    const serviceMode = firstNonEmpty(a?.serviceMode);

                    const sourceType = firstNonEmpty(a?.sourceType);
                    const logisticsDecision = firstNonEmpty(a?.logisticsDecision);
                    const companySize = firstNonEmpty(a?.companySize);

                    const createdAt = a?.created_at ? new Date(a.created_at) : a?.createdAt ? new Date(a.createdAt) : null;
                    const nextActionDate = a?.nextActionDate ? new Date(a.nextActionDate) : null;

                    const laneOriginCountry = firstNonEmpty(a?.laneOrigin?.country, a?.laneOriginCountry, a?.originCountry);
                    const laneOriginPlace = firstNonEmpty(
                        a?.laneOrigin?.placeLabel,
                        a?.laneOriginCityOrPort,
                        a?.originPort,
                        a?.originCity
                    );

                    const laneDestinationCountry = firstNonEmpty(
                        a?.laneDestination?.country,
                        a?.laneDestinationCountry,
                        a?.destinationCountry
                    );

                    const laneDestinationPlace = firstNonEmpty(
                        a?.laneDestination?.placeLabel,
                        a?.laneDestinationCityOrPort,
                        a?.destinationPort,
                        a?.destinationCity
                    );
                    const dateObj = dateField === "nextActionDate" ? nextActionDate : createdAt;
                    const dateStr = dateObj ? dayjs(dateObj).format("YYYY-MM-DD") : "";

                    return {
                        id,
                        customer,
                        leadStatus,
                        tradeType,
                        incoterm,
                        serviceMode,
                        sourceType,
                        logisticsDecision,
                        companySize,
                        laneOriginCountry: laneOriginCountry || "—",
                        laneOriginPlace: laneOriginPlace || "—",
                        laneDestinationCountry: laneDestinationCountry || "—",
                        laneDestinationPlace: laneDestinationPlace || "—",
                        createdAt,
                        nextActionDate,
                        dateStr,
                    };
                });

                setRows(normalized);
            } catch (e) {
                console.error("SalesLeadDashboard fetch error:", e);
                setRows([]);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const rowsWithDateStr = useMemo(() => {
        return (rows || []).map((r) => {
            const dateObj = dateField === "nextActionDate" ? r.nextActionDate : r.createdAt;
            return { ...r, dateStr: dateObj ? dayjs(dateObj).format("YYYY-MM-DD") : "" };
        });
    }, [rows, dateField]);

    // ------------- Date filtering -------------
    const isWithinDateRange = (row) => {
        if (!dateStart && !dateEnd) return true;
        if (!row?.dateStr) return false;

        const d = dayjs(row.dateStr);
        if (dateStart && d.isBefore(dayjs(dateStart).startOf("day"))) return false;
        if (dateEnd && d.isAfter(dayjs(dateEnd).endOf("day"))) return false;
        return true;
    };

    const dateFilteredBase = useMemo(() => {
        return (rowsWithDateStr || []).filter(isWithinDateRange);
    }, [rowsWithDateStr, dateStart, dateEnd]);

    // ------------- Chart filters -------------
    const analyticsRows = useMemo(() => {
        return (dateFilteredBase || []).filter((r) => {
            for (const f of chartFilters || []) {
                if (!f?.type || !f?.value) continue;

                if (f.type === "leadStatus" && r.leadStatus !== f.value) return false;
                if (f.type === "serviceMode" && r.serviceMode !== f.value) return false;
                if (f.type === "tradeType" && r.tradeType !== f.value) return false;
                if (f.type === "sourceType" && r.sourceType !== f.value) return false;
                if (f.type === "originCountry" && r.laneOriginCountry !== f.value) return false;
                if (f.type === "destinationCountry" && r.laneDestinationCountry !== f.value) return false;
                if (f.type === "salesRep") {
                    const names = Array.isArray(r.salesRepNames) ? r.salesRepNames : [];
                    if (!names.includes(f.value)) return false;
                }
                if (f.type === "customer") {
                    if (String(r.customer || "") !== String(f.value)) return false;
                }
                if (f.type === "requestedMonth") {
                    if (!r.dateStr) return false;
                    const m = dayjs(r.dateStr).format("YYYY-MM");
                    if (m !== f.value) return false;
                }
                if (f.type === "requestedWeek") {
                    if (!r.dateStr) return false;
                    const d = dayjs(r.dateStr);
                    const wk = String(d.isoWeek?.() ?? 0).padStart(2, "0");
                    const wy = d.isoWeekYear?.() ?? d.year();
                    const key = `${wy}-W${wk}`;
                    if (key !== f.value) return false;
                }
            }
            return true;
        });
    }, [dateFilteredBase, chartFilters]);
    const tableRows = useMemo(() => {
        return (analyticsRows || []).filter((r) => {
            if (!globalSearch) return true;
            const q = globalSearch.trim().toLowerCase();

            const repsText = (Array.isArray(r.salesRepNames) ? r.salesRepNames.join(" ") : "").toLowerCase();

            return (
                String(r.customer || "").toLowerCase().includes(q) ||
                String(r.leadStatus || "").toLowerCase().includes(q) ||
                String(r.tradeType || "").toLowerCase().includes(q) ||
                String(r.incoterm || "").toLowerCase().includes(q) ||
                String(r.serviceMode || "").toLowerCase().includes(q) ||
                String(r.sourceType || "").toLowerCase().includes(q) ||
                String(r.laneOriginCountry || "").toLowerCase().includes(q) ||
                String(r.laneDestinationCountry || "").toLowerCase().includes(q) ||
                repsText.includes(q)
            );
        });
    }, [analyticsRows, globalSearch]);

    const addFilter = (type, value) => {
        const t = String(type ?? "").trim();
        const v = value == null ? "" : String(value).trim();
        if (!t || !v || v === "undefined" || v === "null") return;
        setChartFilters((prev) => {
            const clean = Array.isArray(prev) ? prev.filter((x) => x?.type && x?.value) : [];
            if (clean.some((x) => x.type === t && x.value === v)) return clean;
            return [...clean, { type: t, value: v }];
        });
    };


    const removeFilter = (f) => {
        if (!f?.type || !f?.value) return;
        setChartFilters((prev) =>
            (Array.isArray(prev) ? prev : [])
                .filter((x) => x?.type && x?.value)
                .filter((x) => !(x.type === f.type && x.value === f.value))
        );
    };

    // ------------- KPIs -------------
    const totals = useMemo(() => {
        const total = analyticsRows.length;

        const byLeadStatus = Object.keys(LEAD_STATUS_META).reduce((acc, k) => {
            acc[k] = analyticsRows.filter((r) => r.leadStatus === k).length;
            return acc;
        }, {});

        const converted = byLeadStatus["Converted"] || 0;
        const conversionRate = total ? Math.round((converted / total) * 100) : 0;
        const uniqCompanies = new Set(analyticsRows.map((r) => r.customer).filter(Boolean)).size;
        const plannedActions = analyticsRows.filter((r) => {
            if (!r.nextActionDate) return false;
            const d = dayjs(r.nextActionDate);
            return d.isSameOrAfter(dayjs(), "day"); // bugün + gelecek
        }).length;
        const avgContacts = total > 0 ? analyticsRows.reduce((s, r) => s + Number(r.contactsCount || 0), 0) / total : 0;
        return {
            total,
            byLeadStatus,
            conversionRate,
            uniqCompanies,
            plannedActions,
            avgContacts,
        };
    }, [analyticsRows]);

    const leadsByStatusData = useMemo(() => {
        const labels = Object.keys(LEAD_STATUS_META);
        const counts = labels.map((s) => analyticsRows.filter((r) => r.leadStatus === s).length);
        return {
            labels,
            datasets: [
                {
                    label: "Leads",
                    data: counts,
                    backgroundColor: labels.map((s) => LEAD_STATUS_META[s].color),
                    borderWidth: 2,
                },
            ],
        };
    }, [analyticsRows]);

    const tradeTypeStats = useMemo(() => {
        const map = {};
        analyticsRows.forEach((r) => {
            const k = cleanStr(r.tradeType) || "Unknown";
            map[k] = (map[k] || 0) + 1;
        });
        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 12);
    }, [analyticsRows]);

    const tradeTypeDoughnutData = useMemo(() => {
        const labels = tradeTypeStats.map((x) => x.label);
        const colors = makeColors(labels.length);
        return {
            labels,
            datasets: [{ label: "Leads", data: tradeTypeStats.map((x) => x.count), backgroundColor: colors, borderWidth: 2 }],
        };
    }, [tradeTypeStats]);

    // ---- Doughnut: Service Mode ----
    const serviceModeStats = useMemo(() => {
        const map = {};
        analyticsRows.forEach((r) => {
            const k = cleanStr(r.serviceMode) || "Unknown";
            map[k] = (map[k] || 0) + 1;
        });
        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 12);
    }, [analyticsRows]);

    const serviceModeDoughnutData = useMemo(() => {
        const labels = serviceModeStats.map((x) => x.label);
        const colors = makeColors(labels.length);
        return {
            labels,
            datasets: [{ label: "Leads", data: serviceModeStats.map((x) => x.count), backgroundColor: colors, borderWidth: 2 }],
        };
    }, [serviceModeStats]);

    // ---- Doughnut: Source Type ----
    const sourceTypeStats = useMemo(() => {
        const map = {};
        analyticsRows.forEach((r) => {
            const k = cleanStr(r.sourceType) || "Unknown";
            map[k] = (map[k] || 0) + 1;
        });
        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 12);
    }, [analyticsRows]);

    const sourceTypeDoughnutData = useMemo(() => {
        const labels = sourceTypeStats.map((x) => x.label);
        const colors = makeColors(labels.length);
        return {
            labels,
            datasets: [{ label: "Leads", data: sourceTypeStats.map((x) => x.count), backgroundColor: colors, borderWidth: 2 }],
        };
    }, [sourceTypeStats]);

    // ---- Doughnut: Origins/Destinations ----
    const topOrigins = useMemo(() => {
        const map = {};
        analyticsRows.forEach((r) => {
            const k = r.laneOriginCountry || "Unknown";
            map[k] = (map[k] || 0) + 1;
        });
        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 8);
    }, [analyticsRows]);

    const topDestinations = useMemo(() => {
        const map = {};
        analyticsRows.forEach((r) => {
            const k = r.laneDestinationCountry || "Unknown";
            map[k] = (map[k] || 0) + 1;
        });
        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 8);
    }, [analyticsRows]);

    // ---- Bar: Sales Rep (column) ----
    const salesRepStats = useMemo(() => {
        const map = {};
        analyticsRows.forEach((r) => {
            const names = Array.isArray(r.salesRepNames) ? r.salesRepNames : [];
            if (!names.length) {
                map["Unassigned"] = (map["Unassigned"] || 0) + 1;
                return;
            }
            names.forEach((n) => {
                const k = cleanStr(n) || "Unassigned";
                map[k] = (map[k] || 0) + 1;
            });
        });

        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 14);
    }, [analyticsRows]);

    const salesRepColumnData = useMemo(() => {
        return {
            labels: salesRepStats.map((x) => x.label),
            datasets: [{ label: "Leads", data: salesRepStats.map((x) => x.count), borderRadius: 10 }],
        };
    }, [salesRepStats]);

    // ---- Bar: Customer (column) ----
    const customerStats = useMemo(() => {
        const map = {};
        analyticsRows.forEach((r) => {
            const k = cleanStr(r.customer) || "Unknown";
            map[k] = (map[k] || 0) + 1;
        });

        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 14);
    }, [analyticsRows]);

    const customerColumnData = useMemo(() => {
        return {
            labels: customerStats.map((x) => x.label),
            datasets: [{ label: "Leads", data: customerStats.map((x) => x.count), borderRadius: 10 }],
        };
    }, [customerStats]);

    // ---- Trends: Month / Week ----
    const requestedMonths = useMemo(() => {
        const map = {};
        analyticsRows.forEach((r) => {
            if (!r.dateStr) return;
            const m = dayjs(r.dateStr).format("YYYY-MM");
            map[m] = (map[m] || 0) + 1;
        });
        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [analyticsRows]);

    const requestedWeeks = useMemo(() => {
        const map = {};
        analyticsRows.forEach((r) => {
            if (!r.dateStr) return;
            const d = dayjs(r.dateStr);
            const wk = String(d.isoWeek?.() ?? 0).padStart(2, "0");
            const wy = d.isoWeekYear?.() ?? d.year();
            const key = `${wy}-W${wk}`;
            map[key] = (map[key] || 0) + 1;
        });

        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [analyticsRows]);

    // ------------- Navigation -------------
    const clickEditHandler = (id) => {
        navigate(`/sales-lead/create-sales-lead/details?id=${id}`);
    };

    // ------------- Table columns -------------
    const columns = useMemo(
        () => [
            {
                Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Customer</span>,
                accessor: "customer",
                Cell: ({ row }) => (
                    <span
                        style={{ color: "blue", textDecoration: "underline", cursor: "pointer", fontWeight: "bold" }}
                        onClick={() => clickEditHandler(row.original.id)}
                        title="Open Lead"
                    >
            {row.original.customer}
          </span>
                ),
            },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Lead Status</span>, accessor: "leadStatus" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Origin</span>, accessor: "laneOriginCountry" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Destination</span>, accessor: "laneDestinationCountry" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Trade Type</span>, accessor: "tradeType" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Incoterm</span>, accessor: "incoterm" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Mode</span>, accessor: "serviceMode" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Source Type</span>, accessor: "sourceType" },
            {
                Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Date</span>,
                accessor: "dateStr",
                Cell: ({ value }) => (value ? dayjs(value).locale("tr").format("DD/MM/YYYY") : "—"),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dateField]
    );

    // ----------------- UI -----------------
    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox pt={3} pb={3}>
                <CrmPageHeader
                    title="Sales Lead Dashboard"
                    subtitle="Lead volume, conversion, companies and planned actions with drill-down charts."
                />
                {loading ? (
                    <MDBox display="flex" justifyContent="center" alignItems="center" height="60vh">
                        <CircularProgress />
                    </MDBox>
                ) : (
                    <Grid container spacing={2.5}>
                        {/* KPI row */}
                        <Grid item xs={12}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <KPICard title="Total Leads" value={totals.total} icon={<TrendingUpIcon fontSize="large" color="primary" />} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <KPICard title="Conversion Rate" value={`${totals.conversionRate}%`} icon={<PublicIcon fontSize="large" color="success" />} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <KPICard title="Unique Companies" value={totals.uniqCompanies} icon={<PeopleIcon fontSize="large" color="warning" />} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <KPICard
                                        title="Planned Actions"
                                        value={totals.plannedActions}
                                        icon={<EventAvailableIcon fontSize="large" color="error" />}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>


                        <Grid item xs={12} md={4}>
                            <Card sx={{ p: 3, height: 350, borderRadius: "10px", boxShadow: "none" }}>
                                <MDTypography variant="h6" sx={{ mb: 2 }}>
                                    Leads by Status
                                </MDTypography>
                                    <Doughnut
                                        data={leadsByStatusData}
                                        options={chartOptions("doughnut", (label) => addFilter("leadStatus", label))}
                                    />
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Card sx={{ p: 3, height: 350, borderRadius: "10px", boxShadow: "none" }}>
                                <MDTypography variant="h6" sx={{ mb: 2 }}>
                                    Leads by Trade Type
                                </MDTypography>
                                <Doughnut
                                    data={tradeTypeDoughnutData}
                                    options={chartOptions("doughnut", (label) => addFilter("tradeType", label))}
                                />
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Card sx={{ p: 3, height: 350, borderRadius: "10px", boxShadow: "none" }}>
                                <MDTypography variant="h6" sx={{ mb: 2 }}>
                                    Leads by Service Mode
                                </MDTypography>
                                <Doughnut
                                    data={serviceModeDoughnutData}
                                    options={chartOptions("doughnut", (label) => addFilter("serviceMode", label))}
                                />
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Card sx={{ p: 3, height: 350, borderRadius: "10px", boxShadow: "none" }}>
                                <MDTypography variant="h6" sx={{ mb: 2 }}>
                                    Leads by Source Type
                                </MDTypography>
                                <Doughnut
                                    data={sourceTypeDoughnutData}
                                    options={chartOptions("doughnut", (label) => addFilter("sourceType", label))}
                                />
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Card sx={{ p: 3, height: 350, borderRadius: "10px", boxShadow: "none" }}>
                                <MDTypography variant="h6" sx={{ mb: 2 }}>
                                    Top Origins
                                </MDTypography>
                                <Doughnut
                                    data={{
                                        labels: topOrigins.map((x) => x.label),
                                        datasets: [{ label: "Leads", data: topOrigins.map((x) => x.count), borderWidth: 2 }],
                                    }}
                                    options={chartOptions("doughnut", (label) => addFilter("originCountry", label))}
                                />
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Card sx={{ p: 3, height: 350, borderRadius: "10px", boxShadow: "none" }}>
                                <MDTypography variant="h6" sx={{ mb: 2 }}>
                                    Top Destinations
                                </MDTypography>
                                <Doughnut
                                    data={{
                                        labels: topDestinations.map((x) => x.label),
                                        datasets: [{ label: "Leads", data: topDestinations.map((x) => x.count), borderWidth: 2 }],
                                    }}
                                    options={chartOptions("doughnut", (label) => addFilter("destinationCountry", label))}
                                />
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    p: 3,
                                    height: 360,
                                    borderRadius: "10px",
                                    boxShadow: "none",
                                    overflow: "hidden",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <MDTypography variant="h6" sx={{ mb: 2 }}>
                                    Leads by Sales Rep
                                </MDTypography>

                                <Box sx={{ flex: 1, minHeight: 0 }}>
                                    <Bar
                                        data={salesRepColumnData}
                                        options={{
                                            indexAxis: "y", // ✅ yatay bar
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: { legend: { display: false } },
                                            scales: {
                                                x: { grid: { color: "#eee" }, ticks: { stepSize: 1 } },
                                                y: {
                                                    grid: { display: false },
                                                    ticks: {
                                                        autoSkip: true,
                                                        maxTicksLimit: 12,
                                                        callback: function (value) {
                                                            const label = this.getLabelForValue(value);
                                                            return String(label).length > 22 ? String(label).slice(0, 22) + "…" : label;
                                                        },
                                                    },
                                                },
                                            },
                                            onClick: (e, elements, chart) => {
                                                if (!elements?.length) return;
                                                const idx = elements[0].index;
                                                const label = chart.data.labels[idx];
                                                if (label) addFilter("salesRep", String(label));
                                            },
                                        }}
                                    />
                                </Box>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card sx={{ p: 3, height: 360, borderRadius: "10px", boxShadow: "none", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                                <MDTypography variant="h6" sx={{ mb: 2 }}>
                                    Leads by Customer
                                </MDTypography>

                                <Box sx={{ flex: 1, minHeight: 0 }}>
                                    <Bar
                                        data={customerColumnData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: { legend: { display: false } },
                                            scales: {
                                                x: {
                                                    grid: { display: false },
                                                    ticks: {
                                                        autoSkip: true,
                                                        maxTicksLimit: 10,
                                                        maxRotation: 45,
                                                        minRotation: 45,
                                                        callback: function (val) {
                                                            const lbl = this.getLabelForValue(val);
                                                            return String(lbl).length > 16 ? String(lbl).slice(0, 16) + "…" : lbl;
                                                        },
                                                    },
                                                },
                                                y: { grid: { color: "#eee" }, ticks: { stepSize: 1 } },
                                            },
                                            onClick: (e, elements, chart) => {
                                                if (!elements?.length) return;
                                                const idx = elements[0].index;
                                                const label = chart.data.labels[idx];
                                                if (label) addFilter("customer", String(label));
                                            },
                                        }}
                                    />
                                </Box>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card sx={{ p: 3, height: 360, borderRadius: "10px", boxShadow: "none", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                                <MDTypography variant="h6" sx={{ mb: 1 }}>
                                    Leads by Week ({dateField})
                                </MDTypography>

                                <Box sx={{ flex: 1, minHeight: 0 }}>
                                    <Line
                                        data={{
                                            labels: requestedWeeks.map((w) => w.label),
                                            datasets: [{ label: "Leads", data: requestedWeeks.map((w) => w.count), tension: 0.35, borderWidth: 3, fill: false }],
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: { legend: { display: false } },
                                            scales: {
                                                x: { grid: { display: false }, ticks: { autoSkip: true, maxTicksLimit: 10, maxRotation: 45, minRotation: 45 } },
                                                y: { grid: { color: "#eee" }, ticks: { stepSize: 1 } },
                                            },
                                            onClick: (e, elements, chart) => {
                                                if (!elements?.length) return;
                                                const idx = elements[0].index;
                                                const label = chart.data.labels[idx];
                                                if (label) addFilter("requestedWeek", label);
                                            },
                                        }}
                                    />
                                </Box>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card sx={{ p: 3, height: 360, borderRadius: "10px", boxShadow: "none", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                                <MDTypography variant="h6" sx={{ mb: 1 }}>
                                    Leads by Month ({dateField})
                                </MDTypography>

                                <Box sx={{ flex: 1, minHeight: 0 }}>
                                    <Line
                                        data={{
                                            labels: requestedMonths.map((m) => m.label),
                                            datasets: [{ label: "Leads", data: requestedMonths.map((m) => m.count), tension: 0.35, borderWidth: 3, fill: false }],
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: { legend: { display: false } },
                                            scales: {
                                                x: { grid: { display: false }, ticks: { autoSkip: true, maxTicksLimit: 8, maxRotation: 45, minRotation: 45 } },
                                                y: { grid: { color: "#eee" }, ticks: { stepSize: 1 } },
                                            },
                                            onClick: (e, elements, chart) => {
                                                if (!elements?.length) return;
                                                const idx = elements[0].index;
                                                const label = chart.data.labels[idx];
                                                if (label) addFilter("requestedMonth", label);
                                            },
                                        }}
                                    />
                                </Box>
                            </Card>
                        </Grid>

                        <Grid item xs={12}>
                            <Card sx={{ p: 3, borderRadius: "10px", boxShadow: "none" }}>
                                {/* Filter chips */}
                                {chartFilters.length > 0 && (
                                    <Box mb={2} display="flex" flexWrap="wrap" gap={1} alignItems="center">
                                        {chartFilters
                                            .filter((f) => f?.type && f?.value)
                                            .map((f, idx) => (
                                                <Chip
                                                    key={`${f.type}-${f.value}-${idx}`}
                                                    label={`${f.type}: ${f.value}`}
                                                    onDelete={() => removeFilter(f)}
                                                    color={safeChipColor(chipColor(f.type))}
                                                    size="small"
                                                    icon={<SearchIcon />}
                                                    sx={{ borderRadius: 2, fontWeight: 800 }}
                                                />
                                            ))}

                                        <Button size="small" onClick={() => setChartFilters([])} sx={{ textTransform: "none", fontWeight: 800 }}>
                                            Clear All
                                        </Button>
                                    </Box>
                                )}

                                {/* Header bar */}
                                <MDBox display="flex" justifyContent="space-between" mb={2} alignItems="center" flexWrap="wrap" gap={2}>
                                    <MDTypography variant="h6" sx={{ fontWeight: 700 }}>
                                        Sales Lead List
                                    </MDTypography>

                                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                        {/* Date Field */}
                                        <FormControl
                                            size="small"
                                            sx={{
                                                width: 220,
                                                "& .MuiInputLabel-root": { fontWeight: 700 },
                                                "& .MuiOutlinedInput-root": { borderRadius: 2, height: 55, fontWeight: 700 },
                                            }}
                                        >
                                            <InputLabel id="datefield-label">Date Field</InputLabel>
                                            <Select labelId="datefield-label" value={dateField} label="Date Field" onChange={(e) => setDateField(e.target.value)}>
                                                <MenuItem value="createdAt">createdAt</MenuItem>
                                                <MenuItem value="nextActionDate">nextActionDate</MenuItem>
                                            </Select>
                                        </FormControl>

                                        {/* Date Start */}
                                        <TextField
                                            size="small"
                                            label="Date Start"
                                            type="date"
                                            value={dateStart}
                                            onChange={(e) => setDateStart(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ minWidth: 170, "& .MuiOutlinedInput-root": { borderRadius: 2, height: 46 } }}
                                        />

                                        {/* Date End */}
                                        <TextField
                                            size="small"
                                            label="Date End"
                                            type="date"
                                            value={dateEnd}
                                            onChange={(e) => setDateEnd(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ minWidth: 170, "& .MuiOutlinedInput-root": { borderRadius: 2, height: 46 } }}
                                        />

                                        {/* Search */}
                                        <TextField
                                            size="small"
                                            placeholder="Search..."
                                            value={globalSearch}
                                            onChange={(e) => setGlobalSearch(e.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{ minWidth: 220, "& .MuiOutlinedInput-root": { borderRadius: 2, height: 46 } }}
                                        />

                                        {(dateStart || dateEnd) && (
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    setDateStart("");
                                                    setDateEnd("");
                                                }}
                                                sx={{ textTransform: "none", fontWeight: 700 }}
                                            >
                                                Clear Dates
                                            </Button>
                                        )}
                                    </Box>
                                </MDBox>

                                <DataTable
                                    table={{
                                        columns,
                                        rows: tableRows,
                                        initialState: { sortBy: [{ id: "dateStr", desc: true }] },
                                    }}
                                    entriesPerPage={{ defaultValue: 100 }}
                                    showTotalEntries
                                    isSorted
                                    sx={{
                                        "& thead": { position: "sticky", top: 0, zIndex: 3 },
                                        "& thead tr th": { backgroundColor: "#fafafa", borderBottom: "1px solid rgba(0,0,0,0.06)" },
                                        "& .MuiTableCell-root": { whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden", maxWidth: 240 },
                                        "& tbody tr:hover": { backgroundColor: "#f7f9fb" },
                                    }}
                                />
                            </Card>
                        </Grid>
                    </Grid>
                )}
            </MDBox>

            <Footer />
        </DashboardLayout>
    );
}
